import * as React from "react"
import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { FileText, ChevronDown, Pencil } from "lucide-react"
import { useTranslation } from "react-i18next"
import { format, isToday, formatDistanceToNow } from "date-fns"
import { es } from 'date-fns/locale'
import { cn, getStatusColor, getPriorityColor } from "@/lib/utils"
import type { AssetRecord } from "@/types/assets"
import { Badge } from "@/components/ui/badge"
import { AppleButton } from "@/components/ui/apple-button"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { AssetRecordForm } from "@/components/forms/AssetRecordForm"
import { toast } from "@/components/ui/use-toast"
import { Link } from "react-router-dom"

interface MiniRecordsProps {
  onRecordChange?: () => void;
}

export function MiniRecords({ onRecordChange }: MiniRecordsProps) {
  const { t, i18n } = useTranslation()
  const pageSize = 15 // Show 15 records
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<AssetRecord | null>(null)

  // Get the current locale for date-fns
  const dateLocale = i18n.language.startsWith('es') ? es : undefined

  const { data: recordsData, refetch: refetchRecords } = useQuery<{
    records: AssetRecord[];
  }>({
    queryKey: ['miniRecords'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('asset_records')
        .select(`
          *,
          assets:asset_id (
            id,
            name,
            asset_name,
            place:place_id (
              id,
              name,
              description
            ),
            company:company_id (
              id,
              name,
              description
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(pageSize)

      if (error) throw error

      return {
        records: data as unknown as AssetRecord[],
      }
    },
  })

  const handleEditClick = (record: AssetRecord) => {
    setSelectedRecord(record)
    setShowEditDialog(true)
  }

  const handleEditSuccess = async () => {
    setShowEditDialog(false)
    setSelectedRecord(null)
    refetchRecords()
    onRecordChange?.()
  }

  if (!recordsData?.records.length) return null

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2 bg-blue-50 border-b border-blue-100">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-600">
          <FileText className="h-3 w-3" />
          <span>{t('records.recentRecords')}</span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto max-h-[400px] scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
        <div className="px-3 py-2 bg-blue-50">
          <Accordion type="single" collapsible className="space-y-1">
            {recordsData.records.map((record) => (
              <AccordionItem 
                key={record.id} 
                value={record.id.toString()}
                className={cn(
                  "border rounded-md overflow-hidden",
                  "bg-white",
                  "shadow-sm hover:shadow-md transition-shadow duration-200"
                )}
              >
                <AccordionTrigger className="px-2 py-1.5 hover:no-underline">
                  <div className="flex flex-col items-start text-left">
                    <div className="font-medium text-xs">
                      {record.title || t('records.untitledRecord')}
                    </div>
                    <div className="text-[10px] text-gray-500 flex flex-col">
                      <div className="flex items-center gap-1">
                        <span>{format(new Date(record.created_at), 'MMM d, yyyy', { locale: dateLocale })}</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-400">
                          {formatDistanceToNow(new Date(record.created_at), { 
                            addSuffix: true,
                            locale: dateLocale 
                          })}
                        </span>
                      </div>
                      {(record.assets?.company || record.assets?.place) && (
                        <span className="text-gray-400">
                          {record.assets?.company?.name}
                          {record.assets?.company && record.assets?.place && " • "}
                          {record.assets?.place?.name}
                        </span>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-2 pb-2 space-y-2">
                  <div className="space-y-2">
                    <div className="space-y-1">
                      <h4 className="font-medium text-xs">{record.title}</h4>
                      {record.details && (
                        <p className="text-xs text-gray-600">{record.details}</p>
                      )}
                    </div>

                    {/* Asset Information */}
                    <div className="space-y-1.5 border-l-2 border-apple-light-gray/30 pl-3">
                      <div className="text-xs uppercase tracking-wider text-apple-gray mb-1">{t('records.assetDetails')}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-apple-blue">
                          {record.assets?.asset_name || record.assets?.name}
                        </span>
                      </div>
                      {record.assets?.company && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-apple-gray">
                            {t('records.company')}: {record.assets.company.name}
                          </span>
                        </div>
                      )}
                      {record.assets?.place && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-apple-gray/80">
                            {t('records.place')}: {record.assets.place.name}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Status and Priority */}
                    <div className="flex flex-wrap gap-1">
                      {record.type && (
                        <Badge variant="outline" className="text-[10px]">
                          {record.type}
                        </Badge>
                      )}
                      {record.status && (
                        <Badge variant="outline" className={cn("text-[10px]", getStatusColor(record.status))}>
                          {record.status}
                        </Badge>
                      )}
                      {record.priority && (
                        <Badge variant="outline" className={cn("text-[10px]", getPriorityColor(record.priority))}>
                          {record.priority}
                        </Badge>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-2 space-y-3">
                      <AppleButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEditClick(record)}
                        className="w-full text-xs py-1.5"
                      >
                        {t('records.edit')}
                      </AppleButton>
                      {record.assets && (
                        <Link to={`/assets/${record.assets.id}`} className="block">
                          <AppleButton
                            variant="secondary"
                            size="sm"
                            className="w-full text-xs py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600"
                          >
                            {t('records.goToAsset')}
                          </AppleButton>
                        </Link>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {/* Edit Record Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>{t('records.editRecord')}</DialogTitle>
              <DialogDescription>
                {t('records.editRecordDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              <AssetRecordForm
                assetId={selectedRecord?.asset_id}
                initialData={selectedRecord ? {
                  id: selectedRecord.id,
                  title: selectedRecord.title,
                  details: selectedRecord.details,
                  type: selectedRecord.type,
                  status: selectedRecord.status,
                  priority: selectedRecord.priority,
                  asset_id: selectedRecord.asset_id,
                  place_id: selectedRecord.place_id,
                  company_id: selectedRecord.company_id,
                  location: selectedRecord.location,
                  assigned_to: selectedRecord.assigned_to,
                  phone: selectedRecord.phone,
                  cost: selectedRecord.cost,
                  due_date: selectedRecord.due_date,
                  approval_status: selectedRecord.approval_status,
                  approval_by: selectedRecord.approval_by,
                  approval_date: selectedRecord.approval_date,
                  alert: selectedRecord.alert,
                  category: selectedRecord.category,
                  notes: selectedRecord.notes,
                  progress: selectedRecord.progress,
                  images: selectedRecord.images,
                  documents: selectedRecord.documents
                } : undefined}
                mode="edit"
                onSuccess={handleEditSuccess}
                onCancel={() => setShowEditDialog(false)}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
} 
